import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { getSessionStore } from '../../store/sessionStore';
import ShareProfileModal from '../ShareProfile/ShareProfileModal';
import { buildShareCandidateInfo } from '../ShareProfile/buildShareCandidateInfo';
import {
  buildCandidateProfilePreviewUrl,
  buildRecruiterProfileUrl,
} from '../../utils/recruiterProfileUrl';
import { computeUserProgress } from '../../utils/sessionProgress';
import { canShareProfile, shareProfileBlockMessage } from '../../utils/userProfile';
import PromptStatsInfoModal from './PromptStatsInfoModal';
import './PromptStatsInfoModal.css';
import {
  loadRolePromptLibrary,
  refreshRolePromptLibrary as fetchFreshRolePrompts,
} from './promptsV2RolePrompts';
import type { RankedPromptChoice } from '../../utils/parseRankedAiPromptList';
import { badgeFromPromptTier } from '../../utils/promptRecommendationBadge';
import {
  createPromptWithMedia,
  deletePromptById,
  fetchPromptAnalytics,
  fetchPromptsList,
  loadPromptsWorkspaceWithAnalytics,
} from './promptsV2Api';
import { SKIPSCREEN_ANALYTICS_UPDATED_EVENT } from '../../utils/realtimeEvents';
import AppBackButton from '../common/AppBackButton';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import type {
  AppPrompt,
  CandidateProfileMeta,
  PromptAnalyticsRow,
  PromptType,
  PromptWizardStep,
} from './promptsV2Types';
import { PromptVideoUploadBar } from './PromptVideoUploadBar';
import PromptEditModal from './PromptEditModal';
import { PromptVideoRecordingModal } from './PromptVideoRecordingModal';
import {
  clearPromptVideoUpload,
  getActivePromptVideoUploads,
  getActiveUploadForPrompt,
  getAnyActiveUpload,
  type PromptVideoUploadPhase,
} from './promptVideoUploadTracker';
import {
  GENERIC_PROMPT_CHOICES,
  hasPromptDescription,
  hasPromptDocument,
  hasPromptVideo,
  isVideoUploadPending,
  overlayClose,
} from './promptsV2Utils';
import { fetchCandidateDashboardMetrics } from '../Dashboard/dashboardApi';
import type { CandidateDashboardMetrics } from '../Dashboard/dashboardApi';
import { PromptsPortfolioWorkspace } from './PromptsPortfolio';
import { PromptViewModal } from './PromptViewModal';
import {
  buildRecommendedActions,
  buildPromptNumberMap,
  computePromptBreakdown,
  computePromptHealthScore,
  countPromptsThisWeek,
  filterAndSortPrompts,
  isPromptNeedsWork,
  sumPromptViews,
  type PromptFilterTab,
  type PromptSortKey,
} from './promptsPortfolioUtils';
import './PromptsV2.css';

const MAX_PROMPTS = 6;

export default function PromptsV2Page() {
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState<CandidateProfileMeta | null>(null);
  const [workspaceUser, setWorkspaceUser] = useState<Record<string, unknown> | null>(null);
  const [rolePromptLibrary, setRolePromptLibrary] = useState<RankedPromptChoice[]>([]);
  const [rolePromptsLoading, setRolePromptsLoading] = useState(false);
  const [rolePromptsRefreshing, setRolePromptsRefreshing] = useState(false);
  const [prompts, setPrompts] = useState<AppPrompt[]>([]);
  const [promptAnalytics, setPromptAnalytics] = useState<PromptAnalyticsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const [promptType, setPromptType] = useState<PromptType>('generic');
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [promptWizardOpen, setPromptWizardOpen] = useState(false);
  const [promptWizardStep, setPromptWizardStep] = useState<PromptWizardStep>(0);
  const [newPromptVideoFile, setNewPromptVideoFile] = useState<File | null>(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string | null>(null);
  const [newPromptDescription, setNewPromptDescription] = useState('');
  const [newPromptDocFile, setNewPromptDocFile] = useState<File | null>(null);
  const [creatingPrompt, setCreatingPrompt] = useState(false);

  const [recordingModalOpen, setRecordingModalOpen] = useState(false);
  const uploadVideoInputRef = useRef<HTMLInputElement | null>(null);

  const [editingPromptId, setEditingPromptId] = useState<number | null>(null);
  const [confirmDeletePromptId, setConfirmDeletePromptId] = useState<number | null>(null);
  const [deletingPrompt, setDeletingPrompt] = useState(false);
  const [viewingPromptId, setViewingPromptId] = useState<number | null>(null);
  const [statsInfoOpen, setStatsInfoOpen] = useState(false);
  const [promptsRefreshing, setPromptsRefreshing] = useState(false);
  const [filterTab, setFilterTab] = useState<PromptFilterTab>('all');
  const [sortKey, setSortKey] = useState<PromptSortKey>('order');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [shareProfileOpen, setShareProfileOpen] = useState(false);
  const [recruiterEmail, setRecruiterEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareEmailSuccess, setShareEmailSuccess] = useState(false);
  const [shareEmailError, setShareEmailError] = useState(false);
  const [shareDurationDays, setShareDurationDays] = useState(30);
  const [dashboardMetrics, setDashboardMetrics] = useState<CandidateDashboardMetrics | null>(null);
  const [videoUploadByPromptId, setVideoUploadByPromptId] = useState<
    Record<number, { progress: number; phase: PromptVideoUploadPhase }>
  >({});
  const [wizardVideoUpload, setWizardVideoUpload] = useState<{
    progress: number;
    phase: PromptVideoUploadPhase;
  } | null>(null);

  const refreshPrompts = useCallback(async () => {
    const [list, analytics] = await Promise.all([fetchPromptsList(), fetchPromptAnalytics()]);
    setPrompts(list);
    setPromptAnalytics(analytics);
    window.dispatchEvent(new Event('prompts-updated'));
  }, []);

  const analyticsByPromptId = useMemo(() => {
    const map = new Map<number, PromptAnalyticsRow>();
    promptAnalytics.forEach((row) => map.set(row.promptId, row));
    return map;
  }, [promptAnalytics]);

  const filteredPrompts = useMemo(
    () =>
      filterAndSortPrompts(prompts, analyticsByPromptId, {
        tab: filterTab,
        sort: sortKey,
        search: searchQuery,
      }),
    [prompts, analyticsByPromptId, filterTab, sortKey, searchQuery]
  );

  const viewingPrompt = useMemo(
    () => prompts.find((p) => p.id === viewingPromptId) ?? null,
    [prompts, viewingPromptId]
  );

  const promptBreakdown = useMemo(() => computePromptBreakdown(prompts, MAX_PROMPTS), [prompts]);

  const promptNumberById = useMemo(() => buildPromptNumberMap(prompts), [prompts]);

  const promptHealthScore = useMemo(() => computePromptHealthScore(prompts), [prompts]);

  const recommendedActions = useMemo(
    () => buildRecommendedActions(prompts, MAX_PROMPTS),
    [prompts]
  );

  const totalPromptViews = useMemo(() => sumPromptViews(promptAnalytics), [promptAnalytics]);

  const publishedThisWeek = useMemo(() => countPromptsThisWeek(prompts), [prompts]);

  const profileLink = useMemo(() => {
    const user = getSessionStore().currentUser as { username?: string } | null;
    const username = user?.username;
    if (!username) return '';
    return buildRecruiterProfileUrl(username);
  }, []);

  const profilePreviewHref = useMemo(() => {
    const user = getSessionStore().currentUser as { username?: string } | null;
    const username = user?.username;
    if (!username) return '';
    return buildCandidateProfilePreviewUrl(username);
  }, []);

  const shareCandidate = useMemo(() => {
    const user = getSessionStore().currentUser as Record<string, unknown> | null;
    const portfolio = getSessionStore().portfolio as {
      overview?: string;
      projects?: unknown[];
    } | null;
    const progress = computeUserProgress(user, prompts, portfolio);
    return buildShareCandidateInfo({
      user,
      jobTitle: String(user?.jobTitle || user?.role_title || ''),
      prompts,
      portfolio,
      profileCompletePercent: progress.percent,
    });
  }, [prompts]);

  const openShareProfileModal = () => {
    if (!canShareProfile(prompts)) {
      toast.error(shareProfileBlockMessage(prompts));
      return;
    }
    setRecruiterEmail('');
    setShareEmailSuccess(false);
    setShareEmailError(false);
    setShareProfileOpen(true);
  };

  const closeShareProfileModal = () => {
    setShareProfileOpen(false);
    setRecruiterEmail('');
    setShareEmailSuccess(false);
    setShareEmailError(false);
  };

  const copyProfileLink = async () => {
    if (!profileLink) {
      toast.error('Profile link is not available yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(profileLink);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const sendProfileToRecruiter = async () => {
    setShareLoading(true);
    setShareEmailSuccess(false);
    setShareEmailError(false);
    try {
      const response = await axiosInstance.post(API_ROUTES.RECRUITER.SHARE_PROFILE, {
        recruiterEmail: recruiterEmail.trim(),
        durationDays: shareDurationDays,
      });
      if (response.status === 200 || response.status === 201) {
        toast.success('Invitation sent successfully!');
        setShareEmailSuccess(true);
        setRecruiterEmail('');
      } else {
        throw new Error('Failed to send invitation');
      }
    } catch {
      setShareEmailError(true);
      toast.error('Failed to send invitation');
    } finally {
      setShareLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadPromptsWorkspaceWithAnalytics();
        if (cancelled) return;
        setProfile(data.profile);
        setWorkspaceUser(data.user);
        setPrompts(data.prompts);
        setPromptAnalytics(data.analytics);
        void fetchCandidateDashboardMetrics().then(setDashboardMetrics);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to load prompts';
        if (!cancelled) {
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncPromptEngagement = async () => {
      setPromptsRefreshing(true);
      try {
        const rows = await fetchPromptAnalytics();
        setPromptAnalytics(rows);
      } finally {
        setPromptsRefreshing(false);
      }
    };
    window.addEventListener(SKIPSCREEN_ANALYTICS_UPDATED_EVENT, syncPromptEngagement);
    return () =>
      window.removeEventListener(SKIPSCREEN_ANALYTICS_UPDATED_EVENT, syncPromptEngagement);
  }, []);

  useEffect(() => {
    const cta = searchParams.get('cta');
    if (!cta) return;
    requestAnimationFrame(() => {
      document
        .getElementById('prompts-workspace')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [searchParams]);

  useEffect(() => {
    const raw = searchParams.get('prompt');
    if (!raw || prompts.length === 0) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return;
    const match = prompts.find((p) => p.id === id);
    if (!match) return;
    setViewingPromptId(id);
    requestAnimationFrame(() => {
      document
        .getElementById(`prompt-card-${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [prompts, searchParams]);

  useEffect(() => {
    const active = getActivePromptVideoUploads();
    if (active.length === 0) return;

    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    const wasReload = nav?.type === 'reload';
    const recentPromptUploads = active.filter(
      (item) => item.promptId != null && Date.now() - item.startedAt < 15 * 60 * 1000
    );

    if (wasReload && recentPromptUploads.length > 0) {
      recentPromptUploads.forEach((item) => clearPromptVideoUpload(item.clientId));
      toast.info(
        'A video upload was in progress when you refreshed. Please upload the video again if it did not finish.'
      );
      return;
    }

    setVideoUploadByPromptId((prev) => {
      const next = { ...prev };
      active.forEach((item) => {
        if (item.promptId != null) {
          next[item.promptId] = {
            progress: item.progress,
            phase: item.phase,
          };
        }
      });
      return next;
    });
  }, []);

  useEffect(() => {
    const hasActiveUpload =
      wizardVideoUpload != null ||
      Object.keys(videoUploadByPromptId).some((id) => {
        const entry = videoUploadByPromptId[Number(id)];
        return entry && entry.phase !== 'done' && entry.phase !== 'error';
      }) ||
      getAnyActiveUpload() != null;

    if (!hasActiveUpload) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [videoUploadByPromptId, wizardVideoUpload]);

  const getPromptUploadProgress = useCallback(
    (promptId: number) =>
      videoUploadByPromptId[promptId] ??
      (() => {
        const session = getActiveUploadForPrompt(promptId);
        return session ? { progress: session.progress, phase: session.phase } : null;
      })(),
    [videoUploadByPromptId]
  );

  const isPromptVideoPending = useCallback(
    (prompt: AppPrompt) =>
      Boolean(getPromptUploadProgress(prompt.id)) || isVideoUploadPending(prompt),
    [getPromptUploadProgress]
  );

  const handleManualRefreshPrompts = useCallback(async () => {
    setPromptsRefreshing(true);
    try {
      await refreshPrompts();
    } finally {
      setPromptsRefreshing(false);
    }
  }, [refreshPrompts]);

  const handlePromptRefresh = useCallback(
    async (prompt: AppPrompt) => {
      const active = getPromptUploadProgress(prompt.id);
      if (active && active.phase !== 'done') {
        toast.info(`Video upload in progress (${active.progress}%)…`);
        return;
      }
      await handleManualRefreshPrompts();
    },
    [getPromptUploadProgress, handleManualRefreshPrompts]
  );

  function applySavedPrompt(updated: AppPrompt) {
    setPrompts((prev) => {
      const index = prev.findIndex((item) => item.id === updated.id);
      if (index === -1) return [...prev, updated];
      return prev.map((item) => (item.id === updated.id ? updated : item));
    });
    window.dispatchEvent(new Event('prompts-updated'));
  }

  const loadRolePrompts = useCallback(async () => {
    if (!profile?.jobTitle?.trim()) {
      setRolePromptLibrary([]);
      return;
    }
    setRolePromptsLoading(true);
    try {
      const library = await loadRolePromptLibrary(profile, { user: workspaceUser });
      setRolePromptLibrary(library);
    } catch {
      setRolePromptLibrary([]);
    } finally {
      setRolePromptsLoading(false);
    }
  }, [profile, workspaceUser]);

  useEffect(() => {
    if (!profile) return;
    void loadRolePrompts();
  }, [profile, loadRolePrompts]);

  const handleRefreshRolePrompts = useCallback(async () => {
    if (!profile?.jobTitle?.trim()) return;
    setRolePromptsRefreshing(true);
    try {
      const library = await fetchFreshRolePrompts(profile);
      setRolePromptLibrary(library);
      if (library.length > 0) {
        toast.success('Generated a fresh set of role-specific prompts.');
      } else {
        toast.error('Could not refresh role-specific prompts. Try again.');
      }
    } catch {
      toast.error('Could not refresh role-specific prompts. Try again.');
    } finally {
      setRolePromptsRefreshing(false);
    }
  }, [profile]);

  useEffect(() => {
    if (promptLibraryOpen && promptType === 'role' && rolePromptLibrary.length === 0) {
      void loadRolePrompts();
    }
  }, [promptLibraryOpen, promptType, rolePromptLibrary.length, loadRolePrompts]);

  const promptChoices = useMemo(
    () => (promptType === 'generic' ? GENERIC_PROMPT_CHOICES : rolePromptLibrary),
    [promptType, rolePromptLibrary]
  );

  const editingPrompt = prompts.find((p) => p.id === editingPromptId) ?? null;

  function resetWizard() {
    setPromptWizardStep(0);
    setNewPromptVideoFile(null);
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPreviewUrl(null);
    setNewPromptDescription('');
    setNewPromptDocFile(null);
    setRecordingModalOpen(false);
  }

  function promptContent(prompt: AppPrompt): string {
    return String(prompt.content || '').trim();
  }

  function isPromptAlreadyUsed(text: string, exceptPromptId?: string | number | null) {
    const normalized = text.trim().toLowerCase();
    if (!normalized) return false;
    return prompts.some((prompt) => {
      if (exceptPromptId != null && prompt.id === exceptPromptId) return false;
      return promptContent(prompt).toLowerCase() === normalized;
    });
  }

  function openPromptLibrary() {
    if (prompts.length >= MAX_PROMPTS) {
      toast.info(`You can have up to ${MAX_PROMPTS} screening prompts.`);
      return;
    }
    setPromptType('generic');
    setSelectedPrompt(null);
    setPromptLibraryOpen(true);
    resetWizard();
  }

  function beginPromptWizard(promptText: string) {
    if (isPromptAlreadyUsed(promptText)) {
      toast.info('This prompt is already in your set.');
      return;
    }
    setSelectedPrompt(promptText);
    setPromptLibraryOpen(false);
    resetWizard();
    setPromptWizardOpen(true);
  }

  function closePromptWizard() {
    setPromptWizardOpen(false);
    setSelectedPrompt(null);
    resetWizard();
  }

  function setUploadedVideo(file: File | null) {
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPreviewUrl(file ? URL.createObjectURL(file) : null);
    setNewPromptVideoFile(file);
  }

  function openRecordingStudio() {
    setRecordingModalOpen(true);
  }

  function handleRecordingCaptured(file: File) {
    setUploadedVideo(file);
    setPromptWizardStep(1);
  }

  async function createPrompt() {
    if (!selectedPrompt?.trim() || !profile) return;
    if (isPromptAlreadyUsed(selectedPrompt)) {
      toast.info('This prompt is already in your set.');
      return;
    }
    setCreatingPrompt(true);
    if (newPromptVideoFile) {
      setWizardVideoUpload({ progress: 0, phase: 'presigning' });
    }
    try {
      const updated = await createPromptWithMedia({
        content: selectedPrompt.trim(),
        description: newPromptDescription.trim(),
        profile,
        videoFile: newPromptVideoFile,
        docFile: newPromptDocFile,
        onVideoProgress: (progress) =>
          setWizardVideoUpload({
            progress: progress.percent,
            phase: progress.phase,
          }),
      });
      closePromptWizard();
      setWizardVideoUpload(null);
      applySavedPrompt(updated);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save prompt');
    } finally {
      setCreatingPrompt(false);
      setWizardVideoUpload(null);
    }
  }

  function openEditModal(prompt: AppPrompt) {
    setEditingPromptId(prompt.id);
  }

  function handlePromptSaved(updated: AppPrompt) {
    setEditingPromptId(null);
    setPrompts((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    window.dispatchEvent(new Event('prompts-updated'));
    void fetchCandidateDashboardMetrics().then(setDashboardMetrics);
  }

  async function deletePrompt(id: number) {
    setDeletingPrompt(true);
    try {
      await deletePromptById(id);
      setConfirmDeletePromptId(null);
      setEditingPromptId(null);
      await refreshPrompts();
      toast.success('Prompt deleted.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete prompt');
    } finally {
      setDeletingPrompt(false);
    }
  }

  if (loading && prompts.length === 0 && !error) {
    return <PageBusyLoader pageName="Prompts" />;
  }

  return (
    <div className="prompts-v2-page" id="prompts-workspace">
      <div className="prompts-v2-page-back">
        <AppBackButton to="/dashboard" />
      </div>
      {error ? (
        <div className="mx-4 mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-800 lg:mx-7">
          {error}
        </div>
      ) : null}

      {loading ? (
        <PageBusyLoader pageName="Prompts" className="page-busy-loader--compact mt-4" />
      ) : (
        <PromptsPortfolioWorkspace
          publishedCount={prompts.length}
          totalViews={totalPromptViews || dashboardMetrics?.profileViews || 0}
          recruitersInterested={dashboardMetrics?.interestedRecruiters ?? 0}
          promptHealthScore={promptHealthScore}
          publishedTrend={publishedThisWeek}
          viewsTrend={dashboardMetrics?.profileViewsBadge ?? 0}
          recruitersTrend={dashboardMetrics?.interestedBadge ?? 0}
          filterTab={filterTab}
          sortKey={sortKey}
          searchQuery={searchQuery}
          filterPanelOpen={filterPanelOpen}
          onFilterTabChange={setFilterTab}
          onSortChange={setSortKey}
          onSearchChange={setSearchQuery}
          onToggleFilter={() => setFilterPanelOpen((v) => !v)}
          onAddPrompt={openPromptLibrary}
          addDisabled={loading || prompts.length >= MAX_PROMPTS}
          prompts={prompts}
          filteredPrompts={filteredPrompts}
          analyticsByPromptId={analyticsByPromptId}
          promptBreakdown={promptBreakdown}
          healthScore={promptHealthScore}
          recommendedActions={recommendedActions}
          onView={(prompt) => setViewingPromptId(prompt.id)}
          onEdit={openEditModal}
          onDelete={(prompt) => setConfirmDeletePromptId(prompt.id)}
          promptNumberById={promptNumberById}
          onActionClick={(action) => {
            if (action.id === 'add-prompt') {
              openPromptLibrary();
              return;
            }

            if (action.id === 'share') {
              openShareProfileModal();
              return;
            }

            const targetPrompt =
              action.id === 'add-video'
                ? prompts.find((p) => !hasPromptVideo(p))
                : action.id === 'add-doc'
                  ? prompts.find((p) => !hasPromptDocument(p))
                  : action.id === 'add-notes'
                    ? prompts.find((p) => !hasPromptDescription(p))
                    : prompts.find(isPromptNeedsWork);

            if (targetPrompt) openEditModal(targetPrompt);
          }}
          renderPromptExtra={(prompt) => {
            const uploadProgress = getPromptUploadProgress(prompt.id);
            const videoUploadPending = isPromptVideoPending(prompt);
            if (!uploadProgress && !videoUploadPending) return null;
            return (
              <div className="mb-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
                {uploadProgress ? (
                  <PromptVideoUploadBar
                    progress={uploadProgress.progress}
                    phase={uploadProgress.phase}
                    compact
                  />
                ) : (
                  <div className="flex items-center justify-between gap-2 text-xs text-sky-900">
                    <span>Video is processing…</span>
                    <button
                      type="button"
                      className="rounded-lg border border-sky-300 bg-white px-2 py-1 text-xs font-semibold"
                      disabled={promptsRefreshing}
                      onClick={() => void handlePromptRefresh(prompt)}
                    >
                      Refresh
                    </button>
                  </div>
                )}
              </div>
            );
          }}
        />
      )}

      {promptLibraryOpen ? (
        <div
          className="pv2-modal-backdrop bg-black/40"
          onMouseDown={(e: MouseEvent<HTMLDivElement>) =>
            overlayClose(e, () => setPromptLibraryOpen(false))
          }
        >
          <div className="pv2-modal-backdrop__center">
            <div
              className="pv2-modal-panel pv2-modal-panel--lg p-4 sm:p-5"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Build your prompt set</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Choose a prompt and bring it to life.
                  </p>
                </div>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setPromptLibraryOpen(false)}
                >
                  Close
                </button>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-b border-emerald-100">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    className={`pb-3 text-sm font-medium ${promptType === 'generic' ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-slate-500'}`}
                    onClick={() => setPromptType('generic')}
                  >
                    General prompts
                  </button>
                  <button
                    type="button"
                    className={`pb-3 text-sm font-medium ${promptType === 'role' ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-slate-500'}`}
                    onClick={() => setPromptType('role')}
                  >
                    Role-specific prompts
                  </button>
                </div>
                {promptType === 'role' ? (
                  <button
                    type="button"
                    className="pv2-btn-secondary mb-2 text-xs sm:text-sm"
                    disabled={
                      rolePromptsLoading || rolePromptsRefreshing || !profile?.jobTitle?.trim()
                    }
                    onClick={() => void handleRefreshRolePrompts()}
                  >
                    {rolePromptsRefreshing ? 'Refreshing…' : 'Refresh role prompts'}
                  </button>
                ) : null}
              </div>

              <div className="mt-4 space-y-3">
                {promptType === 'role' && rolePromptsLoading ? (
                  <p className="py-6 text-center text-sm text-slate-600">
                    Loading your role-specific prompts…
                  </p>
                ) : null}
                {promptType === 'role' && !rolePromptsLoading && promptChoices.length === 0 ? (
                  <div className="rounded-2xl border border-amber-100 bg-amber-50/60 px-4 py-5 text-sm text-amber-900">
                    <p className="font-semibold">No role-specific prompts yet</p>
                    <p className="mt-1 text-amber-800">
                      Role-specific prompts are generated automatically from your resume during
                      onboarding. Tap Refresh role prompts to rebuild the pool for your target role.
                    </p>
                  </div>
                ) : null}
                {promptChoices.map((choice, idx) => {
                  const badge = badgeFromPromptTier(choice.tier);
                  const alreadyUsed = isPromptAlreadyUsed(choice.text);
                  return (
                    <div
                      key={`${idx}-${choice.text.slice(0, 48)}`}
                      className="rounded-2xl border border-emerald-100 bg-white p-4"
                    >
                      {badge ? (
                        <div
                          className={`inline-block rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                            badge.variant === 'most'
                              ? 'bg-amber-100 text-amber-900'
                              : 'bg-amber-50 text-amber-800'
                          }`}
                        >
                          {badge.label}
                        </div>
                      ) : null}
                      <div
                        className={`text-base font-medium text-slate-900 ${badge ? 'mt-3' : ''}`}
                      >
                        {choice.text}
                      </div>
                      <button
                        type="button"
                        className="pv2-btn-primary mt-4"
                        onClick={() => beginPromptWizard(choice.text)}
                        disabled={alreadyUsed}
                        title={alreadyUsed ? 'This prompt is already in your set' : undefined}
                      >
                        {alreadyUsed ? 'Already in your set' : 'Use prompt'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {promptWizardOpen && selectedPrompt ? (
        <div
          className="pv2-modal-backdrop bg-black/40"
          onMouseDown={(e: MouseEvent<HTMLDivElement>) => overlayClose(e, closePromptWizard)}
        >
          <div className="pv2-modal-backdrop__center">
            <div
              className="pv2-modal-panel pv2-modal-panel--md p-4 sm:p-5"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Use prompt</h3>
                  <p className="mt-1 text-sm text-slate-600">{selectedPrompt}</p>
                </div>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-700"
                  onClick={closePromptWizard}
                >
                  Close
                </button>
              </div>

              <div className="mt-4 flex gap-2">
                {[0, 1, 2].map((step) => (
                  <div
                    key={step}
                    className={`h-2 flex-1 rounded-full ${promptWizardStep >= step ? 'bg-emerald-600' : 'bg-emerald-100'}`}
                  />
                ))}
              </div>

              {promptWizardStep === 0 ? (
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <button
                      type="button"
                      className="rounded-2xl border border-emerald-100 bg-white p-5 text-center"
                      onClick={() => openRecordingStudio()}
                    >
                      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-emerald-600 text-white">
                        ▶
                      </div>
                      <div className="font-semibold text-slate-900">Open recording modal</div>
                    </button>
                    <button
                      type="button"
                      className="rounded-2xl border border-emerald-100 bg-white p-5 text-center"
                      onClick={() => uploadVideoInputRef.current?.click()}
                    >
                      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-xl bg-sky-600 text-white">
                        ↑
                      </div>
                      <div className="font-semibold text-slate-900">Upload video</div>
                    </button>
                  </div>
                  <input
                    ref={uploadVideoInputRef}
                    className="hidden"
                    type="file"
                    accept="video/*"
                    aria-label="Upload video file"
                    onChange={(e) => setUploadedVideo(e.target.files?.[0] ?? null)}
                  />
                  {uploadedPreviewUrl ? (
                    <video
                      className="max-h-[360px] w-full rounded-xl border border-slate-200"
                      controls
                      src={uploadedPreviewUrl}
                    >
                      <track kind="captions" />
                    </video>
                  ) : null}
                </div>
              ) : null}

              {promptWizardStep === 1 ? (
                <label className="mt-6 block">
                  <span className="pv2-field-label">
                    Add description or extra details (optional)
                  </span>
                  <textarea
                    className="pv2-input mt-1 min-h-[120px]"
                    value={newPromptDescription}
                    onChange={(e) => setNewPromptDescription(e.target.value)}
                  />
                </label>
              ) : null}

              {promptWizardStep === 2 ? (
                <div className="mt-6 rounded-2xl border border-emerald-100 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-900">
                    Supporting document (optional)
                  </div>
                  <p className="mt-1 text-xs text-slate-600">
                    Attach a resume, case study, or portfolio sample.
                  </p>
                  <label className="pv2-btn-secondary mt-3 cursor-pointer">
                    <input
                      className="hidden"
                      type="file"
                      accept=".pdf,.doc,.docx,image/*"
                      onChange={(e) => setNewPromptDocFile(e.target.files?.[0] ?? null)}
                    />
                    {newPromptDocFile ? 'Change file' : 'Choose file'}
                  </label>
                  {newPromptDocFile ? (
                    <p className="mt-2 text-xs text-slate-600">{newPromptDocFile.name}</p>
                  ) : null}
                </div>
              ) : null}

              {wizardVideoUpload && creatingPrompt ? (
                <div className="mt-6">
                  <PromptVideoUploadBar
                    progress={wizardVideoUpload.progress}
                    phase={wizardVideoUpload.phase}
                  />
                </div>
              ) : null}

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  className="pv2-btn-secondary"
                  onClick={() => {
                    if (promptWizardStep === 0) {
                      closePromptWizard();
                      return;
                    }
                    setPromptWizardStep((prev) => (prev - 1) as PromptWizardStep);
                  }}
                >
                  {promptWizardStep === 0 ? 'Cancel' : 'Previous'}
                </button>
                <div className="flex gap-2">
                  {promptWizardStep < 2 ? (
                    <>
                      <button
                        type="button"
                        className="pv2-btn-secondary"
                        onClick={() =>
                          setPromptWizardStep((prev) => (prev + 1) as PromptWizardStep)
                        }
                      >
                        Skip
                      </button>
                      <button
                        type="button"
                        className="pv2-btn-primary"
                        onClick={() =>
                          setPromptWizardStep((prev) => (prev + 1) as PromptWizardStep)
                        }
                      >
                        Next
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="pv2-btn-primary"
                      disabled={creatingPrompt}
                      onClick={() => void createPrompt()}
                    >
                      {creatingPrompt
                        ? wizardVideoUpload
                          ? `Uploading ${wizardVideoUpload.progress}%…`
                          : 'Saving…'
                        : 'Save prompt'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <PromptVideoRecordingModal
        show={recordingModalOpen}
        promptText={selectedPrompt ?? ''}
        onHide={() => setRecordingModalOpen(false)}
        onVideoCaptured={handleRecordingCaptured}
      />

      <PromptViewModal prompt={viewingPrompt} onClose={() => setViewingPromptId(null)} />

      <PromptStatsInfoModal open={statsInfoOpen} onClose={() => setStatsInfoOpen(false)} />

      <PromptEditModal
        prompt={editingPrompt}
        onClose={() => setEditingPromptId(null)}
        onSaved={handlePromptSaved}
        onDeleteRequest={setConfirmDeletePromptId}
      />

      {confirmDeletePromptId != null ? (
        <div
          className="pv2-modal-backdrop bg-black/40"
          onMouseDown={(e: MouseEvent<HTMLDivElement>) =>
            overlayClose(e, () => setConfirmDeletePromptId(null))
          }
        >
          <div className="pv2-modal-backdrop__center">
            <div
              className="pv2-modal-panel pv2-modal-panel--sm p-5"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-semibold text-slate-900">Delete prompt response?</h3>
              <p className="mt-2 text-sm text-slate-600">This action cannot be undone.</p>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  className="pv2-btn-secondary"
                  onClick={() => setConfirmDeletePromptId(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pv2-btn-danger"
                  disabled={deletingPrompt}
                  onClick={() => void deletePrompt(confirmDeletePromptId)}
                >
                  {deletingPrompt ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ShareProfileModal
        show={shareProfileOpen}
        onHide={closeShareProfileModal}
        profileLink={profileLink}
        recruiterEmail={recruiterEmail}
        onRecruiterEmailChange={setRecruiterEmail}
        onCopyLink={copyProfileLink}
        onSendEmail={sendProfileToRecruiter}
        sending={shareLoading}
        sendSuccess={shareEmailSuccess}
        sendError={shareEmailError}
        previewHref={profilePreviewHref}
        durationDays={shareDurationDays}
        onDurationDaysChange={setShareDurationDays}
        candidate={shareCandidate}
      />
    </div>
  );
}
